import { auth } from '../firebase';
import { StockAdviceReport, StockAdviceRequest, StockAdviceResponse } from '../types/stockAdvice';

const endpointBase =
  import.meta.env?.VITE_AI_ENDPOINT_BASE_URL ||
  import.meta.env?.VITE_MARKET_DATA_ENDPOINT_BASE_URL ||
  import.meta.env?.VITE_QUOTE_ENDPOINT_BASE_URL ||
  '';

const getFunctionUrls = (functionName: 'stockAdvice') => {
  if (!endpointBase) return [`/${functionName}`];

  const candidates: string[] = [];

  try {
    const url = new URL(endpointBase);
    if (url.hostname.endsWith('.a.run.app') && url.hostname.includes('-')) {
      const hostParts = url.hostname.split('-');
      hostParts[0] = functionName.toLowerCase();
      url.hostname = hostParts.join('-');
      url.pathname = '/';
      url.search = '';
      candidates.push(url.toString());
    }
  } catch {
    // ignore and fall back to path strategy
  }

  candidates.push(`${endpointBase.replace(/\/+$/, '')}/${functionName}`);

  return [...new Set(candidates)];
};

const parseJsonResponse = async (response: Response) => {
  const body = await response.text();
  try {
    return body ? JSON.parse(body) : null;
  } catch {
    if (!response.ok) {
      throw new Error(`Stock advice request failed (${response.status}).`);
    }
    throw new Error('Stock advice response was not valid JSON.');
  }
};

export const fetchStockAdvice = async (input: StockAdviceRequest): Promise<StockAdviceResponse> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const urls = getFunctionUrls('stockAdvice');

  console.info('[stockAdviceService] request:start', {
    urls,
    symbol: input.symbol,
    instrumentId: input.instrumentId,
    exchange: input.exchange,
  });

  let response: Response | null = null;
  let networkError: unknown = null;

  for (const url of urls) {
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });
      networkError = null;
      break;
    } catch (error) {
      networkError = error;
      console.warn('[stockAdviceService] request:network-error', {
        url,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  if (!response) {
    if (networkError instanceof Error) {
      throw new Error('Unable to reach AI advice service right now. Please try again in a moment.');
    }
    throw new Error('Failed to fetch stock advice report.');
  }

  const rawJSON = await parseJsonResponse(response);
  console.info('[stockAdviceService] request:done', {
    status: response.status,
    ok: response.ok,
    rawResponseKeys: rawJSON && typeof rawJSON === 'object' ? Object.keys(rawJSON) : [],
    rawReportKeys: rawJSON?.report && typeof rawJSON.report === 'object' ? Object.keys(rawJSON.report) : [],
    rawReportFundamentalKeys: rawJSON?.report?.fundamental && typeof rawJSON.report.fundamental === 'object' ? Object.keys(rawJSON.report.fundamental) : [],
    rawReportTradePlanKeys: rawJSON?.report?.tradePlan && typeof rawJSON.report.tradePlan === 'object' ? Object.keys(rawJSON.report.tradePlan) : [],
  });
  if (!response.ok) {
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const retryMessage = retryAfter
        ? `Rate limit reached. Please try again in about ${retryAfter} seconds.`
        : 'Rate limit reached. Please wait a moment before refreshing again.';
      throw new Error(retryMessage);
    }
    throw new Error(rawJSON?.error || 'Failed to fetch stock advice report.');
  }

  return {
    cached: Boolean(rawJSON?.cached),
    report: rawJSON?.report as StockAdviceReport,
  };
};
